/**
 * Input — text / email / tel / url / password / search / number.
 *
 * Theme T5a §2.  Labels are always visible (placeholder-as-label is
 * banned for accessibility); wrap in <FormField> for full label + helper
 * + error composition.
 *
 * Error state is controlled by `aria-invalid` (prop) — keeps the
 * component presentation-agnostic.  Callers drive it from form state.
 */
import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

import { cn } from './cn';

export type InputSize = 'sm' | 'md';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  inputSize?: InputSize;
  /** Leading content — typically a currency symbol or short unit. */
  prefix?: ReactNode;
  /** Trailing content — typically a unit or count. */
  suffix?: ReactNode;
  /** Trigger the "success" border + icon — use sparingly. */
  success?: boolean;
}

const SIZE_CLASSES: Record<InputSize, string> = {
  sm: 'h-8 text-sm',
  md: 'h-10 text-base',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { inputSize = 'md', prefix, suffix, success, className, disabled, readOnly, ...rest },
  ref,
) {
  const invalid = rest['aria-invalid'] === true || rest['aria-invalid'] === 'true';

  const shell = cn(
    'flex w-full items-center gap-2 rounded-md border bg-white transition-colors duration-fast ease-standard',
    'border-slate-300 hover:border-slate-400',
    'focus-within:border-brand-primary-500 focus-within:ring-2 focus-within:ring-brand-primary-400/50 focus-within:ring-offset-0',
    invalid && 'border-error-600 focus-within:border-error-600 focus-within:ring-error-600/50',
    success && !invalid && 'border-success-600 focus-within:border-success-600',
    disabled && 'bg-slate-50 opacity-disabled cursor-not-allowed',
    readOnly && 'bg-slate-50',
    className,
  );

  return (
    <div className={shell}>
      {prefix ? (
        <span className="flex h-full items-center self-stretch border-r border-slate-200 bg-slate-50 px-3 text-sm text-slate-600">
          {prefix}
        </span>
      ) : null}
      <input
        ref={ref}
        className={cn(
          'min-w-0 flex-1 bg-transparent px-3 text-slate-800 placeholder:text-fg-muted focus:outline-none',
          SIZE_CLASSES[inputSize],
          prefix && 'pl-0',
          suffix && 'pr-0',
        )}
        disabled={disabled}
        readOnly={readOnly}
        {...rest}
      />
      {suffix ? (
        <span className="flex h-full items-center self-stretch border-l border-slate-200 bg-slate-50 px-3 text-sm text-slate-600">
          {suffix}
        </span>
      ) : null}
    </div>
  );
});
